/**
 * BuyerMultiSelect — the buyers covering one supplier-customer relationship.
 *
 * A supplier account is commonly covered by more than one buyer (a lead plus
 * cover, or a split by commodity), so this is a set rather than the single
 * name the settings screen used to hold.
 *
 * Shown as tags: each assigned buyer is a chip you remove with its own x, and
 * a + opens a searchable list of the people not yet assigned. The chips make
 * the answer readable at a glance in a table cell — "who covers this account"
 * is the question, and a count or a truncated join makes you open the control
 * to find out.
 *
 * The candidates are the *customer's* users: a buyer works for the company
 * doing the buying, not for the supplier being bought from.
 */
import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Search, X, Loader2 } from 'lucide-react';
import { UserProfile } from '../../lib/api';

interface BuyerMultiSelectProps {
  /** Users assignable as a buyer for this relationship's customer. */
  options: UserProfile[];
  /** Currently assigned buyer user ids. */
  selected: number[];
  /**
   * Names to show for assigned buyers who are not in `options` — someone set
   * by the ERP or an earlier admin who has since left the customer's user
   * list. Without these their chip would render as a bare id.
   */
  fallbackNames?: string[];
  disabled?: boolean;
  saving?: boolean;
  onChange: (selected: number[]) => void;
}

export function BuyerMultiSelect({
  options, selected, fallbackNames = [], disabled, saving, onChange,
}: BuyerMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const nameFor = (u: UserProfile) => u.user.full_name || u.user.email;

  /** Chips to draw: known users first, then any unmatched fallback names. */
  const chips = useMemo(() => {
    const known = selected
      .map((id) => {
        const match = options.find((u) => u.user.id === id);
        return match ? { id, name: nameFor(match) } : null;
      })
      .filter((v): v is { id: number; name: string } => v !== null);
    const unmatchedCount = selected.length - known.length;
    const strays = unmatchedCount > 0
      ? fallbackNames.slice(0, unmatchedCount).map((name, i) => ({ id: -(i + 1), name }))
      : [];
    return [...known, ...strays];
  }, [selected, options, fallbackNames]);

  /** Only offer people who are not already assigned. */
  const addable = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options
      .filter((u) => !selected.includes(u.user.id))
      .filter((u) => !q || nameFor(u).toLowerCase().includes(q));
  }, [options, selected, query]);

  const add = (id: number) => {
    onChange([...selected, id]);
    setQuery('');
    setOpen(false);
  };

  // A stray chip has a synthetic negative id and no real user to remove, so it
  // is shown but not removable — removing it would guess at whose id it was.
  const remove = (id: number) => {
    if (id < 0) return;
    onChange(selected.filter((v) => v !== id));
  };

  return (
    <div className="flex flex-wrap items-center gap-1">
      {chips.length === 0 && (
        <span className="text-muted-foreground italic text-sm">Not assigned</span>
      )}

      {chips.map((chip) => (
        <span
          key={chip.id}
          className="inline-flex items-center gap-1 rounded-full border bg-muted/60 pl-2.5 pr-1 py-0.5 text-xs max-w-[180px]"
        >
          <span className="truncate">{chip.name}</span>
          {!disabled && chip.id > 0 && (
            <button
              type="button"
              onClick={() => remove(chip.id)}
              aria-label={`Remove ${chip.name}`}
              className="rounded-full p-0.5 hover:bg-muted-foreground/20"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}

      {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}

      {!disabled && (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 rounded-full"
              aria-label="Add a buyer"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[240px] p-0" align="start">
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Find a name"
                  className="h-8 pl-7 text-sm"
                  aria-label="Find a buyer by name"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-[220px] overflow-y-auto p-1">
              {addable.length === 0 ? (
                <p className="text-sm text-muted-foreground italic px-2 py-3">
                  {options.length === 0
                    ? 'No users for this company'
                    : query ? 'No match' : 'Everyone is already assigned'}
                </p>
              ) : addable.map((u) => (
                <button
                  key={u.user.id}
                  type="button"
                  onClick={() => add(u.user.id)}
                  className="w-full text-left truncate px-2 py-1.5 rounded-sm hover:bg-muted text-sm"
                >
                  {nameFor(u)}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
